import dayjs from "dayjs";
import dayOfYear from "dayjs/plugin/dayOfYear";
import duration from "dayjs/plugin/duration";
import objectSupport from "dayjs/plugin/objectSupport";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekday from "dayjs/plugin/weekday";
import { makeAutoObservable } from "mobx";
import { cronIterator, QuartzObject } from "..";
dayjs.extend(objectSupport);
dayjs.extend(weekday);
dayjs.extend(dayOfYear);
dayjs.extend(weekOfYear);
dayjs.extend(duration);
export class CronState {
    /**
     * @name CronState
     * @author Nico Vitt
     * @description The format referred to is the one by Quarts Scheduler. For further information follow these links.
     * http://www.quartz-scheduler.org/documentation/quartz-2.3.0/tutorials/crontrigger.html and
     * https://www.freeformatter.com/cron-expression-generator-quartz.html
     */
    constructor() {
        this.timeSeries = "once";
        this.quartzobject = new QuartzObject(this.startdate);
        this.diffunit = "day";
        // public iteratorRangeInDays: number = 0;
        // public formfieldvalues: FormFieldTypes = {
        //   daily: undefined,
        //   monthly_day: undefined,
        //   monthly_date: undefined,
        //   monthly_series: undefined,
        //   day_of_week: undefined,
        //   weekday_select: undefined,
        //   weekly: undefined,
        //   yearly: undefined,
        // };
        this._onlyWeeks = true;
        this._weekInterval = 1;
        this._cronstring = "";
        this._cronstringcopied = false;
        this._selectedMonthInCalendar = dayjs().get("month");
        this._selectedYearInCalendar = dayjs().get("year");
        this._startdate = dayjs().second(0).minute(0).hour(8);
        this._currentEnddate = undefined;
        this._monthlySwitcher = 1;
        // private iter: Generator<Dayjs> = undefined;
        this.regex = new RegExp(
        // eslint-disable-next-line
        /^\s*($|#|\w+\s*=|(?<SECOND>(?:\?|\*|(?:(?<START_SEC>[0-5]?\d)(?:(?<DELIMITER_SEC>\-|\/|\,)(?<END_SEC>[0-6]?\d))*)))\s+(?<MINUTE>(?:\?|\*|(?:(?<START_MIN>[0-5]?\d)(?:(?<DELIMITER_MIN>\-|\/|\,)(?<END_MIN>[0-6]?\d))*)))\s+(?<HOUR>(?:\?|\*|(?:(?<START_HR>[0-2]?\d)(?:(?<DELIMITER_HR>\-|\/|\,)(?<END_HR>[0-2]?\d))*)))\s+(?<DAYOFMONTH>\?|\*|(?:(?<START_DOM>L|W|LW|[1-3]?\dW?)(?:(?<DELIMITER_DOM>\-|\/|\,)(?<END_DOM>[1-3]?\d?W?))*))\s+(?<MONTH>\*|(?:(?<START_MON>[1-9]|1[012]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(?:(?<DELIMITER_MON>\-|\/|\,)(?<END_MON>[1-9]|1[012]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC))*))\s+(?<DAYOFWEEK>\?|\*|(?:(?<START_DOW>[1-7]|SUN|MON|TUE|WED|THU|FRI|SAT)(?:(?:(?:(?<DELIMITER_DOW>\/|\,|\#)(?<END_DOW>[1-7]|SUN|MON|TUE|WED|THU|FRI|SAT))*)L?)*))(?:|\s)+(?<YEAR>(?:\*|(?:(?<START_YR>\b(?:[1-2](?:9|0)\d\d)\b)(?:(?<DELIMITER_YR>\-|\/|\,)(?<END_YR>\b(?:[1-9]|[1-9][0-9]|1[0-8][0-9]|19[0-2])\b|\b(?:[1-2](?:9|0)\d\d)\b))*))))$/);
        makeAutoObservable(this);
        // this.iter = cronIterator(this);
    }
    get cronstring() {
        return this._cronstring;
    }
    set cronstring(v) {
        this._cronstring = v;
    }
    get weekInterval() {
        return this._weekInterval;
    }
    set weekInterval(v) {
        this._weekInterval = v;
    }
    get cronstringcopied() {
        return this._cronstringcopied;
    }
    set cronstringcopied(v) {
        this._cronstringcopied = v;
    }
    get selectedMonthInCalendar() {
        return this._selectedMonthInCalendar;
    }
    set selectedMonthInCalendar(v) {
        this._selectedMonthInCalendar = v;
    }
    get selectedYearInCalendar() {
        return this._selectedYearInCalendar;
    }
    set selectedYearInCalendar(v) {
        this._selectedYearInCalendar = v;
    }
    get startdate() {
        return this._startdate;
    }
    set startdate(v) {
        this.quartzobject.updateStartdate(v);
        this._startdate = v;
    }
    get currentEnddate() {
        return this._currentEnddate;
    }
    set currentEnddate(v) {
        this._currentEnddate = v;
    }
    get monthlySwitcher() {
        return this._monthlySwitcher;
    }
    set monthlySwitcher(v) {
        if (v === 1) {
            this.quartzobject.dayofweek = "?";
        }
        if (v === 2) {
            this.quartzobject.dayofmonth = "?";
        }
        this._monthlySwitcher = v;
    }
    get onlyWeeks() {
        return this._onlyWeeks;
    }
    set onlyWeeks(v) {
        if (v) {
            //When onlyWeeks is true set this.quartzobject.dayofmonth to the date() of monday in this.startdate
            this.quartzobject.dayofmonth = dayjs(this.startdate)
                .weekday(1)
                .date()
                .toString();
            //When this.timeSeries is TimeSeriesType.daily set this.timeSeries to TimeSeriesType.weekly
            if (this.timeSeries === "daily") {
                this.timeSeries = "weekly";
            }
        }
        this._onlyWeeks = v;
    }
    /**
     * @description A function which takes  as input the name of the field of QuartzObject which should be set with a specific value.
     * The function checks the rules specified by QuartzObject and sets the field accordingly.
     * @param field The field to update identified by RegexGroups
     * @param value The value to set the field to
     */
    setField(field, value) {
        // console.log("---------");
        // console.log("called setField", field, value);
        //Find the field in this.quartzobject.rules identified by field
        const ruleset = this.quartzobject.rules.find((entry) => entry.field === field);
        if (ruleset) {
            const fieldvalue = ruleset.modify(value, this);
            // console.log("returned value", field, fieldvalue);
            // console.log("---------");
            this.quartzobject[ruleset.setter] = fieldvalue;
            ruleset.rules.forEach((rule) => {
                this.quartzobject[rule.field] = rule.modify();
            });
        }
    }
    /**
     * Reset the current state to the initial state.
     */
    resetState() {
        this.quartzobject.reset();
        this._cronstring = "";
        this._cronstringcopied = false;
        this._currentEnddate = undefined;
        this._monthlySwitcher = 1;
        this._selectedMonthInCalendar = dayjs().get("month");
        this._selectedYearInCalendar = dayjs().get("year");
        this._startdate = dayjs().second(0).minute(0).hour(8);
    }
    /**
     * @description Provides a string representation of the cron object.
     * @returns string
     */
    objectToString() {
        this._cronstring = this.quartzobject.toString();
        this._cronstringcopied = false;
        return this.quartzobject.toString();
    }
    /**
     * @description Checks whether the passed date fits the passed cron string.
     * @param date
     * @returns boolean
     */
    checkDateAgainstCronString(cronstring, date) {
        this.cronStringToState(cronstring, date.get("month"));
        return this.fitsDate(date);
    }
    /**
     * @description Checks whether the passed date fits the current state (cron object).
     * @param value
     * @returns
     */
    fitsDate(value) {
        // console.log("-----------------");
        // console.log("fitsDate", value.format("YYYY-MM-DD HH:mm:ss"));
        // Only for visible dates
        if (this.selectedMonthInCalendar !== value.get("month") &&
            this.selectedYearInCalendar === value.get("year") &&
            // Not before start date
            !this.startdate.isBefore(dayjs(value).add(1, "day"), "day") &&
            // Not after end date
            this.currentEnddate &&
            value.isAfter(this.currentEnddate, "day")) {
            return false;
        }
        // Single Date
        if (this.timeSeries === "once") {
            return value.isSame(this.startdate, this.onlyWeeks ? "week" : "day");
        }
        const limit = typeof this.currentEnddate !== "undefined"
            ? this.currentEnddate.diff(this.startdate, this.diffunit)
            : dayjs({
                year: this.selectedYearInCalendar,
                month: this.selectedMonthInCalendar,
            })
                .add(1, "month")
                .endOf("month")
                .diff(this.startdate, this.diffunit);
        //Check if value is in the cron expression
        const iterator = cronIterator(this, limit);
        let nextresult = iterator.next();
        while (!nextresult.done) {
            // for (let nextresult of cronIterator(this, limit)) {
            // console.log("next result", nextresult);
            if (nextresult.value.isSame(value, this.onlyWeeks ? "week" : "day")) {
                iterator.return(nextresult.value);
                return true;
            }
            nextresult = iterator.next();
        }
        return false;
    }
    /**
     * This function returns the next dates fitting the cron string.
     * @param amount The number of dates to return.
     * @returns An array of Dayjs objects fitting the cron string.
     */
    nextDates(amount) {
        const dates = [];
        const limit = typeof this.currentEnddate !== "undefined"
            ? this.currentEnddate.diff(this.startdate, this.diffunit)
            : dayjs("2099-12-31").diff(this.startdate, this.diffunit);
        const iterator = cronIterator(this, limit);
        let nextresult = iterator.next();
        while (!nextresult.done) {
            // console.log("nextDates", nextresult);
            if (dates.length >= amount) {
                iterator.return(nextresult.value);
            }
            dates.push(nextresult.value);
            nextresult = iterator.next();
        }
        return dates;
    }
    /**
     * TODO
     * @description Gets the form value from cron string.
     * @param formfield
     */
    getFormValueFromCronString(formfield) {
        let calculated = -1;
        let [start, delimiter, end, full] = ["", "", "", ""];
        switch (formfield) {
            case "daily":
                [start, delimiter, end, full] = this.getFieldsFromCronString([
                    "START_DOM",
                    "DELIMITER_DOM",
                    "END_DOM",
                    "DAYOFMONTH",
                ]);
                //If delimiter equals / return end
                //If delimiter equals - return end
                //If delimiter equals , split full by delimiter and return field 1 minus field 2 calculated by dayjs diff on days
                if (delimiter === "/") {
                    calculated = parseInt(end);
                }
                else if (delimiter === "-") {
                    calculated = parseInt(end);
                }
                else if (delimiter === ",") {
                    calculated = dayjs(full.split(",")[1]).diff(dayjs(full.split(",")[0]), "day");
                }
                break;
            case "weekly":
                calculated = 1;
                break;
            case "monthly_day":
                calculated = 1;
                break;
            case "monthly_date":
                calculated = 1;
                break;
            case "monthly_series":
                calculated = 1;
                break;
            case "yearly":
                [start, delimiter, end, full] = this.getFieldsFromCronString([
                    "START_YR",
                    "DELIMITER_YR",
                    "END_YR",
                    "YEAR",
                ]);
                //If delimiter equals / return end
                //If delimiter equals - return 1
                //If delimiter equals , split full by delimiter and return field 1 minus field 2 calculated by dayjs diff on years
                if (delimiter === "/") {
                    calculated = parseInt(end);
                }
                else if (delimiter === "-") {
                    calculated = 1;
                }
                else if (delimiter === ",") {
                    calculated = dayjs(full.split(",")[1]).diff(dayjs(full.split(",")[0]), "year");
                }
                break;
            case "day_of_week":
                calculated = 1;
                break;
            default:
                break;
        }
        if (calculated === -1) {
            return this.quartzobject.getInitialFormValue(formfield);
        }
        else {
            return calculated;
        }
    }
    /**
     * @description Returns the specified fields in the cron string.
     * @param fields as Array<string>
     */
    getFieldsFromCronString(fields) {
        const cronregex = this.cronstring.match(this.regex);
        if (cronregex === null) {
            return [];
        }
        const regexgroups = cronregex.groups;
        return fields.map((field) => regexgroups[field]);
    }
    /**
     * @description Fills the state with the values from the cron string.
     * @param cronstring
     * @param date
     */
    cronStringToState(cronstring, month) {
        const cronregex = cronstring.match(this.regex);
        if (cronregex === null) {
            return false;
        }
        const regexgroups = cronregex.groups;
        //Fill the state values
        this._cronstring = cronstring;
        this._cronstringcopied = false;
        this._currentEnddate = undefined;
        this._monthlySwitcher = 1;
        this._selectedMonthInCalendar =
            typeof month === "undefined" ? dayjs().month() : month;
        this.quartzobject.dayofmonth = regexgroups.DAYOFMONTH;
        this.quartzobject.dayofweek = regexgroups.DAYOFWEEK;
        this.quartzobject.seconds = regexgroups.SECOND;
        this.quartzobject.minutes = regexgroups.MINUTE;
        this.quartzobject.hours = regexgroups.HOUR;
        this.quartzobject.month = regexgroups.MONTH;
        this.quartzobject.year = regexgroups.YEAR;
        this.quartzobject.DELIMITER_DOM = regexgroups.DELIMITER_DOM;
        this.quartzobject.DELIMITER_DOW = regexgroups.DELIMITER_DOW;
        this.quartzobject.DELIMITER_HR = regexgroups.DELIMITER_HR;
        this.quartzobject.DELIMITER_MIN = regexgroups.DELIMITER_MIN;
        this.quartzobject.DELIMITER_MON = regexgroups.DELIMITER_MON;
        this.quartzobject.DELIMITER_SEC = regexgroups.DELIMITER_SEC;
        this.quartzobject.DELIMITER_YR = regexgroups.DELIMITER_YR;
        this.quartzobject.END_DOM = regexgroups.END_DOM;
        this.quartzobject.END_DOW = regexgroups.END_DOW;
        this.quartzobject.END_HR = regexgroups.END_HR;
        this.quartzobject.END_MIN = regexgroups.END_MIN;
        this.quartzobject.END_MON = regexgroups.END_MON;
        this.quartzobject.END_SEC = regexgroups.END_SEC;
        this.quartzobject.END_YR = regexgroups.END_YR;
        this.quartzobject.START_DOM = regexgroups.START_DOM;
        this.quartzobject.START_DOW = regexgroups.START_DOW;
        this.quartzobject.START_HR = regexgroups.START_HR;
        this.quartzobject.START_MIN = regexgroups.START_MIN;
        this.quartzobject.START_MON = regexgroups.START_MON;
        this.quartzobject.START_SEC = regexgroups.START_SEC;
        this.quartzobject.START_YR = regexgroups.START_YR;
        return true;
    }
}
//# sourceMappingURL=CronState.js.map